"use strict";
/**
 * Shared DocSync/Persist failure taxonomy.
 *
 * Keep this package pure and dependency-free. The nbstore client and
 * @streamlain/server both import this contract, while runtime policy remains
 * in their owning packages.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DOC_SYNC_STORAGE_FULL_RETRY_AFTER_MS = exports.SELF_HEAL_RETRY_MAX_BACKOFF_MS = exports.classifyDocSyncWireError = exports.DOC_SYNC_PERSIST_ERROR_PREFIX = exports.DOC_SYNC_WIRE_ERROR_NAME = exports.DOC_SYNC_FAILURE_CLASS_METADATA = exports.DOC_SYNC_CLIENT_LOCAL_POISON_REJECT_THRESHOLD = exports.DOC_SYNC_FAILURE_CLASSES = exports.DOC_SYNC_FAILURE_CLASS = exports.MAX_DOC_BYTES = exports.MAX_GROUP_BYTES = exports.MAX_GROUP_DOCS = exports.DOC_SYNC_CLIENT_ACK_TIMEOUT_MS = void 0;
exports.isDocSyncFailureClass = isDocSyncFailureClass;
exports.getDocSyncFailureClassMetadata = getDocSyncFailureClassMetadata;
exports.isDocSyncPermanentlyDeletedWireError = isDocSyncPermanentlyDeletedWireError;
exports.isDocSyncAccessDeniedWireError = isDocSyncAccessDeniedWireError;
exports.isDocSyncProducerBugInvariantLastError = isDocSyncProducerBugInvariantLastError;
exports.classifyDocPersistOutboxState = classifyDocPersistOutboxState;
exports.classifyDocSyncPushError = classifyDocSyncPushError;
exports.selfHealRetryBackoffMs = selfHealRetryBackoffMs;
/** Shared socket acknowledgement deadline; admission must finish before this. */
exports.DOC_SYNC_CLIENT_ACK_TIMEOUT_MS = 10_000;
exports.MAX_GROUP_DOCS = 64;
exports.MAX_GROUP_BYTES = 256 * 1024 * 1024;
// Technical per-document group budget, matching the native merged-snapshot cap.
// Separate from database-identity's 10 MB product limit and limits.config.ts's
// 20 MB legacy health / 25 MB wire update / 32 MiB transport limits.
exports.MAX_DOC_BYTES = 64 * 1024 * 1024;
exports.DOC_SYNC_FAILURE_CLASS = {
    ReadBusy: 'read_busy',
    PermissionLag: 'permission_lag',
    DatabaseRootNotReady: 'database_root_not_ready',
    RecoverableOutboxBlock: 'recoverable_outbox_block',
    ProducerBugInvariant: 'producer_bug_invariant',
    PersistedPoison: 'persisted_poison',
    ClientLocalPoison: 'client_local_poison',
    UnrecoverableCorruption: 'unrecoverable_corruption',
    StorageFull: 'storage_full',
};
exports.DOC_SYNC_FAILURE_CLASSES = Object.freeze(Object.values(exports.DOC_SYNC_FAILURE_CLASS));
exports.DOC_SYNC_CLIENT_LOCAL_POISON_REJECT_THRESHOLD = 3;
exports.DOC_SYNC_FAILURE_CLASS_METADATA = Object.freeze({
    [exports.DOC_SYNC_FAILURE_CLASS.ReadBusy]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.ReadBusy,
        healingPath: 'retry',
        userVisibility: 'invisible',
        operatorLoud: false,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.PermissionLag]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.PermissionLag,
        healingPath: 'retry',
        userVisibility: 'invisible',
        operatorLoud: false,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.DatabaseRootNotReady]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.DatabaseRootNotReady,
        healingPath: 'retry',
        userVisibility: 'invisible',
        operatorLoud: false,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.RecoverableOutboxBlock]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.RecoverableOutboxBlock,
        healingPath: 'self_heal',
        userVisibility: 'invisible',
        operatorLoud: true,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.ProducerBugInvariant]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.ProducerBugInvariant,
        healingPath: 'repair_contract',
        userVisibility: 'invisible',
        operatorLoud: true,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.PersistedPoison]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.PersistedPoison,
        healingPath: 'repair_contract',
        userVisibility: 'invisible',
        operatorLoud: true,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.ClientLocalPoison]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.ClientLocalPoison,
        healingPath: 'client_rebuild',
        userVisibility: 'invisible',
        operatorLoud: false,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.UnrecoverableCorruption]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.UnrecoverableCorruption,
        healingPath: 'operator',
        userVisibility: 'visible',
        operatorLoud: true,
    },
    [exports.DOC_SYNC_FAILURE_CLASS.StorageFull]: {
        failureClass: exports.DOC_SYNC_FAILURE_CLASS.StorageFull,
        healingPath: 'retry',
        userVisibility: 'visible',
        operatorLoud: false,
    },
});
const DOC_SYNC_FAILURE_CLASS_SET = new Set(exports.DOC_SYNC_FAILURE_CLASSES);
function isDocSyncFailureClass(value) {
    return typeof value === 'string' && DOC_SYNC_FAILURE_CLASS_SET.has(value);
}
function getDocSyncFailureClassMetadata(failureClass) {
    return exports.DOC_SYNC_FAILURE_CLASS_METADATA[failureClass];
}
exports.DOC_SYNC_WIRE_ERROR_NAME = {
    ReadBusy: 'DOC_SYNC_READ_BUSY',
    AccessDenied: 'ACCESS_DENIED',
    Forbidden: 'FORBIDDEN',
    DatabaseRootNotReady: 'DATABASE_ROOT_NOT_READY',
    DocPermanentlyDeleted: 'DOC_PERMANENTLY_DELETED',
    DocPersistOutboxBlocked: 'DOC_PERSIST_OUTBOX_BLOCKED',
    CorruptSnapshot: 'CorruptSnapshotError',
    DocHealthCritical: 'DOC_HEALTH_CRITICAL',
    StorageFull: 'STORAGE_FULL',
};
function isDocSyncPermanentlyDeletedWireError(error) {
    return (readWireErrorName(error) === exports.DOC_SYNC_WIRE_ERROR_NAME.DocPermanentlyDeleted);
}
exports.DOC_SYNC_PERSIST_ERROR_PREFIX = {
    InvariantViolation: 'POISON invariant_violation',
    DatabaseRootInvariant: 'POISON database_root_invariant',
};
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function readStringProperty(value, key) {
    if (!isRecord(value)) {
        return null;
    }
    const property = value[key];
    return typeof property === 'string' && property.trim() ? property : null;
}
function hasTrueFlag(value, keys) {
    return isRecord(value) && keys.some(key => value[key] === true);
}
function readWireErrorName(error) {
    return (readStringProperty(error, 'name') ??
        readStringProperty(error, 'code') ??
        readStringProperty(error, 'type'));
}
function isAccessDeniedWireErrorName(name) {
    return (name === exports.DOC_SYNC_WIRE_ERROR_NAME.AccessDenied ||
        name === exports.DOC_SYNC_WIRE_ERROR_NAME.Forbidden);
}
function isDocSyncAccessDeniedWireError(error) {
    return isAccessDeniedWireErrorName(readWireErrorName(error));
}
function isOutboxQuarantined(input) {
    return (input.quarantined === true ||
        input.quarantinedAt instanceof Date ||
        (typeof input.quarantinedAt === 'string' &&
            input.quarantinedAt.trim() !== ''));
}
function isDocSyncProducerBugInvariantLastError(value) {
    return (typeof value === 'string' &&
        Object.values(exports.DOC_SYNC_PERSIST_ERROR_PREFIX).some(prefix => value.startsWith(prefix)));
}
function classifyDocPersistOutboxState(input) {
    if (isDocSyncProducerBugInvariantLastError(input.lastError)) {
        return exports.DOC_SYNC_FAILURE_CLASS.ProducerBugInvariant;
    }
    if (input.unrecoverable === true || input.corruption === true) {
        return exports.DOC_SYNC_FAILURE_CLASS.UnrecoverableCorruption;
    }
    if (input.poison === true || isOutboxQuarantined(input)) {
        return exports.DOC_SYNC_FAILURE_CLASS.PersistedPoison;
    }
    if (input.blocked === true && input.recoverable === true) {
        return exports.DOC_SYNC_FAILURE_CLASS.RecoverableOutboxBlock;
    }
    return null;
}
function classifyDocSyncPushError(error, options = {}) {
    const explicitFailureClass = readStringProperty(error, 'failureClass');
    if (isDocSyncFailureClass(explicitFailureClass)) {
        return explicitFailureClass;
    }
    const name = readWireErrorName(error);
    if (options.unrecoverableCorruption ||
        name === exports.DOC_SYNC_WIRE_ERROR_NAME.CorruptSnapshot ||
        name === exports.DOC_SYNC_WIRE_ERROR_NAME.DocHealthCritical ||
        hasTrueFlag(error, ['unrecoverable', 'corruption'])) {
        return exports.DOC_SYNC_FAILURE_CLASS.UnrecoverableCorruption;
    }
    if (name === exports.DOC_SYNC_WIRE_ERROR_NAME.StorageFull)
        return exports.DOC_SYNC_FAILURE_CLASS.StorageFull;
    if (name === exports.DOC_SYNC_WIRE_ERROR_NAME.ReadBusy)
        return exports.DOC_SYNC_FAILURE_CLASS.ReadBusy;
    if (name === exports.DOC_SYNC_WIRE_ERROR_NAME.DatabaseRootNotReady) {
        return exports.DOC_SYNC_FAILURE_CLASS.DatabaseRootNotReady;
    }
    if (options.recoverableOutboxBlock) {
        return exports.DOC_SYNC_FAILURE_CLASS.RecoverableOutboxBlock;
    }
    const outboxFailureClass = classifyDocPersistOutboxState({
        lastError: readStringProperty(error, 'lastError'),
        blocked: hasTrueFlag(error, ['blocked']),
        recoverable: hasTrueFlag(error, ['recoverable', 'retryable']),
        quarantined: hasTrueFlag(error, ['quarantined']),
        quarantinedAt: isRecord(error) ? error.quarantinedAt : undefined,
    });
    if (outboxFailureClass) {
        return outboxFailureClass;
    }
    if (name === exports.DOC_SYNC_WIRE_ERROR_NAME.DocPersistOutboxBlocked) {
        return exports.DOC_SYNC_FAILURE_CLASS.RecoverableOutboxBlock;
    }
    if (options.persistedPoison || hasTrueFlag(error, ['quarantined'])) {
        return exports.DOC_SYNC_FAILURE_CLASS.PersistedPoison;
    }
    if (isAccessDeniedWireErrorName(name) &&
        (options.pendingAccessBinding ||
            options.retryablePermissionLag ||
            hasTrueFlag(error, [
                'pendingAccessBinding',
                'pendingBinding',
                'bindingPending',
                'retryable',
            ]))) {
        return exports.DOC_SYNC_FAILURE_CLASS.PermissionLag;
    }
    if (options.clientLocalPoison ||
        hasTrueFlag(error, ['localOnly', 'clientLocalPoison'])) {
        return exports.DOC_SYNC_FAILURE_CLASS.ClientLocalPoison;
    }
    return null;
}
exports.classifyDocSyncWireError = classifyDocSyncPushError;
/**
 * Self-healing retry schedule of transient doc-sync failures, shared by the
 * browser sync client and the doc-persist worker (Rust mirror in
 * `packages/doc-persist-worker/src/main.rs`):
 * `min(base * 2^(attempt - 1), 30 days)`. Retries run seconds, then minutes,
 * hours, days and weeks apart and finally every 30 days, so data a user needs
 * now heals within seconds while nothing is ever given up on.
 */
exports.SELF_HEAL_RETRY_MAX_BACKOFF_MS = 2_592_000_000;
/**
 * Retry time of a write refused because the workspace storage is full.
 * Space returns only by deletion or a plan change, never by waiting, so the
 * retry does not grow like the self-healing schedule.
 */
exports.DOC_SYNC_STORAGE_FULL_RETRY_AFTER_MS = 60_000;
function selfHealRetryBackoffMs(attempt, baseMs) {
    const exponent = Math.min(Math.max(Math.floor(attempt) - 1, 0), 40);
    return Math.min(baseMs * 2 ** exponent, exports.SELF_HEAL_RETRY_MAX_BACKOFF_MS);
}
//# sourceMappingURL=index.js.map